import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Eye, PanelRightOpen, PanelRightClose } from 'lucide-react';
import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
    useDroppable,
} from '@dnd-kit/core';
import CVPreview from './CVPreview';
import PageOrganizerPanel, { OrganizerDragPayload } from './PageOrganizerPanel';
import ContentBlockDragHandles from './ContentBlockDragHandles';
import { CVData, SectionKey } from '../../types/cv';
import {
    A4_PAGE_HEIGHT_PX,
    A4_PAGE_WIDTH_PX,
    SECTION_LABELS,
    buildPageData,
    compactPageCount,
    countMainContentItems,
    ensureCVIdsAndLayout,
    findOverflowCandidate,
    findPullBackCandidate,
    getSectionItems,
    hasUserStartedEditing,
    isMainFlowMonotonic,
    isSidebarFlowMonotonic,
    moveItemToPage,
    pageHasContent,
    resetAllItemsToFirstPage,
    resetMainFlowToFirstPage,
} from '../../utils/cvPagination';

interface BuilderPreviewProps {
    data: CVData;
    template: string;
    onMoveItem: (itemId: string, pageIndex: number) => void;
    onMoveSection: (section: SectionKey, pageIndex: number) => void;
    onSetPageCount: (pageCount: number) => void;
    onResetLayout?: () => void;
    onReplaceData?: (data: CVData) => void;
    /** Tutoriel : forcer l’ouverture du panneau Organisation */
    tourOpenOrganizer?: boolean;
}

const PageDropWrapper: React.FC<{
    pageIndex: number;
    children: React.ReactNode;
    enabled: boolean;
    data: CVData;
}> = ({ pageIndex, children, enabled, data }) => {
    const hostRef = useRef<HTMLDivElement>(null);
    const { setNodeRef, isOver } = useDroppable({
        id: `preview-page-${pageIndex}`,
        data: { pageIndex },
        disabled: !enabled,
    });

    const setRefs = (node: HTMLDivElement | null) => {
        hostRef.current = node;
        setNodeRef(node);
    };

    return (
        <div
            ref={setRefs}
            className={`relative transition-shadow ${enabled && isOver ? 'ring-2 ring-indigo-400 ring-offset-2 rounded-sm' : ''}`}
        >
            {children}
            <ContentBlockDragHandles rootRef={hostRef} enabled={enabled} data={data} />
            {enabled && isOver && (
                <div className="absolute inset-0 bg-indigo-500/10 pointer-events-none flex items-center justify-center z-30">
                    <span className="bg-indigo-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow">
                        Déposer sur la page {pageIndex + 1}
                    </span>
                </div>
            )}
        </div>
    );
};

const resolveDropPageIndex = (overId: string): number | null => {
    if (overId.startsWith('preview-page-')) {
        return Number(overId.replace('preview-page-', ''));
    }
    if (overId.startsWith('page-drop-')) {
        return Number(overId.replace('page-drop-', ''));
    }
    return null;
};

const OVERFLOW_TOLERANCE_PX = 14;
/** Marge mini pour tenter un pull-back (évite les micro-oscillations). */
const MIN_FREE_RATIO = 0.035;
/** Réserve titre de section si l'item tire une nouvelle section sur la page. */
const SECTION_HEADING_RESERVE_PX = 36;

/**
 * Aperçu A4 réel (210×297mm), mis à l'échelle pour tenir dans le panneau.
 * Pagination : un seul flux vertical — remplir chaque page, ne déplacer que le surplus.
 */
const BuilderPreview: React.FC<BuilderPreviewProps> = ({
    data,
    template,
    onMoveItem,
    onMoveSection,
    onSetPageCount,
    onReplaceData,
    tourOpenOrganizer = false,
}) => {
    const ensured = ensureCVIdsAndLayout(data);
    const userEditing = hasUserStartedEditing(ensured);
    const [activeLabel, setActiveLabel] = useState<string | null>(null);
    const [showOrganizer, setShowOrganizer] = useState(false);
    const [scale, setScale] = useState(1);
    const autoBusy = useRef(false);
    /** Évite de re-tirer un item qui vient d'être rejeté (ne rentre pas). */
    const rejectedPull = useRef<Set<string>>(new Set());
    const viewportRef = useRef<HTMLDivElement>(null);

    const pageCount = Math.max(1, ensured.layout?.pageCount || 1);

    useEffect(() => {
        if (!tourOpenOrganizer) return;
        setShowOrganizer(true);
        if (onReplaceData && pageCount < 2) {
            onReplaceData({
                ...ensured,
                layout: {
                    ...ensured.layout!,
                    pageCount: Math.max(pageCount, 2),
                },
            });
        }
    }, [tourOpenOrganizer]); // eslint-disable-line react-hooks/exhaustive-deps

    const pagesToRender = Array.from({ length: pageCount }, (_, i) => i).filter(
        (pageIndex) => pageIndex === 0 || pageHasContent(ensured, pageIndex) || showOrganizer
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 180, tolerance: 8 },
        })
    );

    // Persiste les ids manquants — sinon ils sont régénérés à chaque rendu et le drag casse
    useLayoutEffect(() => {
        if (!onReplaceData) return;
        const lacksId = (arr?: { id?: string }[]) => (arr || []).some((item) => !item?.id);
        if (
            !lacksId(data.experience) &&
            !lacksId(data.education) &&
            !lacksId(data.skills) &&
            !lacksId(data.languages) &&
            !lacksId(data.interests) &&
            !lacksId(data.references) &&
            data.layout
        ) {
            return;
        }
        onReplaceData(ensureCVIdsAndLayout(data));
    }, [data, onReplaceData]);

    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;
        const update = () => {
            const available = el.clientWidth - 16;
            if (available < 40) return; // panneau masqué (onglet Formulaire mobile)
            const next = Math.min(1, available / A4_PAGE_WIDTH_PX);
            setScale(Number.isFinite(next) && next > 0.2 ? next : 1);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Contenu ou template changé → autoriser à nouveau les pull-backs
    useEffect(() => {
        rejectedPull.current.clear();
    }, [
        ensured.experience?.length,
        ensured.education?.length,
        ensured.languages?.length,
        ensured.interests?.length,
        ensured.references?.length,
        ensured.skills?.length,
        template,
    ]);

    // Changement de modèle → repartir page 1 (ordre de flux différent)
    const prevTemplate = useRef(template);
    useLayoutEffect(() => {
        if (!onReplaceData || showOrganizer) return;
        if (prevTemplate.current === template) return;
        prevTemplate.current = template;
        rejectedPull.current.clear();
        autoBusy.current = true;
        onReplaceData(resetAllItemsToFirstPage(ensured));
        requestAnimationFrame(() => {
            autoBusy.current = false;
        });
    }, [template, onReplaceData, showOrganizer, ensured]);

    useLayoutEffect(() => {
        if (!onReplaceData || showOrganizer || !userEditing) return;
        if (countMainContentItems(ensured) === 0 && !(ensured.skills?.length > 0)) return;
        if (autoBusy.current) return;
        if (prevTemplate.current !== template) return;

        /** Limite = bas utile de la feuille (évite de paginer alors qu’il reste du blanc). */
        const columnLimit = (col: HTMLElement) => {
            const colBottom = col.getBoundingClientRect().bottom;
            const sheet = col.closest('.cv-page-sheet') as HTMLElement | null;
            if (!sheet) return colBottom;
            const sr = sheet.getBoundingClientRect();
            const sheetLimit = sr.bottom - Math.max(28, sr.height * 0.04);
            return Math.min(colBottom, sheetLimit);
        };

        const colOverflows = (col: HTMLElement | null) => {
            if (!col) return false;
            const limit = columnLimit(col);
            const blocks = col.querySelectorAll<HTMLElement>('[data-cv-block]');
            if (blocks.length === 0) {
                return (
                    col.scrollHeight > col.clientHeight + OVERFLOW_TOLERANCE_PX &&
                    col.clientHeight > 40
                );
            }
            for (const el of Array.from(blocks)) {
                const r = el.getBoundingClientRect();
                if (r.height < 4) continue;
                if (r.bottom > limit + OVERFLOW_TOLERANCE_PX) return true;
            }
            return false;
        };

        const freeSpacePx = (col: HTMLElement) => {
            const limit = columnLimit(col);
            const colTop = col.getBoundingClientRect().top;
            const blocks = col.querySelectorAll<HTMLElement>('[data-cv-block]');
            let bottom = colTop;
            for (const el of Array.from(blocks)) {
                const r = el.getBoundingClientRect();
                if (r.height < 4) continue;
                bottom = Math.max(bottom, r.bottom);
            }
            return limit - bottom;
        };

        const readPageIndex = (host: HTMLElement) => {
            const raw = host.getAttribute('data-cv-page');
            const n = raw == null ? NaN : Number(raw);
            return Number.isFinite(n) ? n : null;
        };

        const commit = (next: CVData) => {
            autoBusy.current = true;
            onReplaceData(compactPageCount(next));
            requestAnimationFrame(() => {
                autoBusy.current = false;
            });
        };

        const run = () => {
            if (
                !isMainFlowMonotonic(ensured, template) ||
                !isSidebarFlowMonotonic(ensured, template)
            ) {
                rejectedPull.current.clear();
                commit(resetMainFlowToFirstPage(ensured, template));
                return;
            }

            const pageNodes = Array.from(
                document.querySelectorAll<HTMLElement>('#printable-cv-area [data-cv-page]')
            );
            if (pageNodes.length === 0) return;

            const sheetOf = (host: HTMLElement) =>
                (host.querySelector('.cv-page-sheet') as HTMLElement | null) || host;

            // 1) Débordement : descendre le DERNIER item du flux
            for (const host of pageNodes) {
                const pageIndex = readPageIndex(host);
                if (pageIndex == null) continue;
                const sheet = sheetOf(host);
                const sidebar = sheet.querySelector<HTMLElement>('[data-cv-sidebar]');
                const main = sheet.querySelector<HTMLElement>('[data-cv-main]');

                if (main && colOverflows(main)) {
                    const candidate = findOverflowCandidate(ensured, pageIndex, 'main', template);
                    if (candidate) {
                        rejectedPull.current.add(`${pageIndex}:${candidate.itemId}`);
                        commit(moveItemToPage(ensured, candidate.itemId, pageIndex + 1));
                        return;
                    }
                }

                if (sidebar && colOverflows(sidebar)) {
                    const candidate = findOverflowCandidate(ensured, pageIndex, 'sidebar', template);
                    if (candidate) {
                        rejectedPull.current.add(`${pageIndex}:${candidate.itemId}`);
                        commit(moveItemToPage(ensured, candidate.itemId, pageIndex + 1));
                        return;
                    }
                }
            }

            // 2) Remonter le PREMIER item de la page suivante s'il RENTRE dans le blanc
            for (const host of pageNodes) {
                const pageIndex = readPageIndex(host);
                if (pageIndex == null) continue;
                const sheet = sheetOf(host);
                const sidebar = sheet.querySelector<HTMLElement>('[data-cv-sidebar]');
                const main = sheet.querySelector<HTMLElement>('[data-cv-main]');
                const pageH = sheet.getBoundingClientRect().height || A4_PAGE_HEIGHT_PX;
                const minFree = Math.max(28, pageH * MIN_FREE_RATIO);

                if (main && !colOverflows(main)) {
                    const free = freeSpacePx(main);
                    if (free > minFree) {
                        const pull = findPullBackCandidate(ensured, pageIndex + 1, 'main', template);
                        if (pull) {
                            const rejectKey = `${pageIndex}:${pull.itemId}`;
                            if (!rejectedPull.current.has(rejectKey)) {
                                const nextHost = pageNodes.find((n) => readPageIndex(n) === pageIndex + 1);
                                const nextMain = nextHost
                                    ? sheetOf(nextHost).querySelector<HTMLElement>('[data-cv-main]')
                                    : null;
                                const pullEl = nextMain?.querySelector<HTMLElement>(
                                    `[data-cv-item-id="${pull.itemId}"]`
                                );
                                const pullH = pullEl?.getBoundingClientRect().height ?? 0;

                                const pageData = buildPageData(ensured, pageIndex);
                                const sectionAlready =
                                    (pull.section === 'education' && (pageData.education?.length || 0) > 0) ||
                                    (pull.section === 'experience' && (pageData.experience?.length || 0) > 0) ||
                                    (pull.section === 'languages' && (pageData.languages?.length || 0) > 0) ||
                                    (pull.section === 'interests' && (pageData.interests?.length || 0) > 0) ||
                                    (pull.section === 'references' && (pageData.references?.length || 0) > 0);
                                const headingExtra = sectionAlready
                                    ? 0
                                    : SECTION_HEADING_RESERVE_PX * (pageH / A4_PAGE_HEIGHT_PX);
                                const need = (pullH > 0 ? pullH : free * 0.5) + headingExtra + 8;

                                if (pullH > 0 && need > free) {
                                    rejectedPull.current.add(rejectKey);
                                } else {
                                    commit(moveItemToPage(ensured, pull.itemId, pageIndex));
                                    return;
                                }
                            }
                        }
                    }
                }

                if (sidebar && !colOverflows(sidebar)) {
                    const free = freeSpacePx(sidebar);
                    if (free > minFree) {
                        const pull = findPullBackCandidate(ensured, pageIndex + 1, 'sidebar', template);
                        if (pull) {
                            const rejectKey = `side:${pageIndex}:${pull.itemId}`;
                            if (!rejectedPull.current.has(rejectKey)) {
                                const nextHost = pageNodes.find((n) => readPageIndex(n) === pageIndex + 1);
                                const nextSide = nextHost
                                    ? sheetOf(nextHost).querySelector<HTMLElement>('[data-cv-sidebar]')
                                    : null;
                                const pullEl = nextSide?.querySelector<HTMLElement>(
                                    `[data-cv-item-id="${pull.itemId}"]`
                                );
                                const pullH = pullEl?.getBoundingClientRect().height ?? 0;
                                if (pullH > 0 && pullH + 8 > free) {
                                    rejectedPull.current.add(rejectKey);
                                } else {
                                    commit(moveItemToPage(ensured, pull.itemId, pageIndex));
                                    return;
                                }
                            }
                        }
                    }
                }
            }
        };

        const t = window.setTimeout(() => requestAnimationFrame(run), 60);
        return () => clearTimeout(t);
    }, [ensured, template, showOrganizer, userEditing, onReplaceData, pagesToRender.length, scale]);

    useEffect(() => {
        const onBeforePrint = () => {
            if (showOrganizer) setShowOrganizer(false);
        };
        window.addEventListener('beforeprint', onBeforePrint);
        return () => window.removeEventListener('beforeprint', onBeforePrint);
    }, [showOrganizer]);

    const handleDragStart = (event: DragStartEvent) => {
        const payload = event.active.data.current as OrganizerDragPayload | undefined;
        if (!payload) return;
        if (payload.kind === 'section') {
            setActiveLabel(SECTION_LABELS[payload.section]);
        } else {
            const items = getSectionItems(ensured, payload.section);
            setActiveLabel(items.find((i) => i.id === payload.itemId)?.label || 'Élément');
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveLabel(null);
        const { active, over } = event;
        if (!over) return;

        const overData = over.data.current as { pageIndex?: number } | undefined;
        let pageIndex =
            typeof overData?.pageIndex === 'number'
                ? overData.pageIndex
                : resolveDropPageIndex(String(over.id));

        if (pageIndex === null || Number.isNaN(pageIndex)) return;

        const payload = active.data.current as OrganizerDragPayload | undefined;
        if (!payload) return;

        if (pageIndex > 0 && pageCount < pageIndex + 1) {
            onSetPageCount(pageIndex + 1);
        }
        if (payload.kind === 'section') onMoveSection(payload.section, pageIndex);
        else onMoveItem(payload.itemId, pageIndex);
    };

    const scaledHeight = A4_PAGE_HEIGHT_PX * scale;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <div className="flex flex-1 min-w-0 overflow-hidden print:overflow-visible" data-tour="preview">
                <div
                    ref={viewportRef}
                    className="flex flex-col flex-1 bg-slate-200/50 p-2 sm:p-4 overflow-y-auto print:p-0 print:bg-white print:overflow-visible"
                >
                    <div className="flex items-center justify-between mb-3 sm:mb-4 px-1 sm:px-2 print:hidden no-print gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <Eye className="w-4 h-4 text-slate-500 shrink-0" />
                            <span className="text-sm font-semibold text-slate-600">Aperçu A4</span>
                            <span className="text-xs text-slate-400 truncate hidden sm:inline">
                                {pagesToRender.length} page{pagesToRender.length > 1 ? 's' : ''} · 210×297 mm
                            </span>
                        </div>
                        <button
                            type="button"
                            data-tour="organizer"
                            onClick={() => {
                                setShowOrganizer((v) => {
                                    if (!v && onReplaceData && pageCount < 2) {
                                        onReplaceData({
                                            ...ensured,
                                            layout: {
                                                ...ensured.layout!,
                                                pageCount: Math.max(pageCount, 2),
                                            },
                                        });
                                    }
                                    return !v;
                                });
                            }}
                            className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 shrink-0"
                            title="Organisation manuelle multi-pages (optionnel)"
                        >
                            {showOrganizer ? (
                                <PanelRightClose className="w-3.5 h-3.5" />
                            ) : (
                                <PanelRightOpen className="w-3.5 h-3.5" />
                            )}
                            Organisation
                        </button>
                    </div>

                    {pagesToRender.length > 1 && !showOrganizer && (
                        <div className="mb-3 mx-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 no-print print:hidden">
                            Page pleine — le surplus passe automatiquement à la page suivante. Ouvrez{' '}
                            <button
                                type="button"
                                className="font-bold underline"
                                onClick={() => setShowOrganizer(true)}
                            >
                                Organisation
                            </button>{' '}
                            pour glisser sections et éléments entre les pages.
                        </div>
                    )}

                    <div
                        id="printable-cv-area"
                        className="mx-auto print:space-y-0"
                        style={{ width: A4_PAGE_WIDTH_PX * scale }}
                    >
                        {pagesToRender.map((pageIndex) => {
                            const pageData = buildPageData(ensured, pageIndex);
                            const isContinuation = pageIndex > 0;
                            if (isContinuation && !pageHasContent(ensured, pageIndex) && !showOrganizer) {
                                return null;
                            }
                            return (
                                <div
                                    key={pageIndex}
                                    className="cv-page-wrapper mb-8 print:mb-0"
                                    style={{
                                        width: A4_PAGE_WIDTH_PX * scale,
                                        height: scaledHeight,
                                    }}
                                >
                                    <div className="flex items-center justify-between mb-2 px-1 print:hidden no-print">
                                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                            Page {pageIndex + 1} / {pagesToRender.length}
                                        </span>
                                    </div>
                                    <div
                                        className="origin-top-left print:transform-none"
                                        style={{
                                            width: A4_PAGE_WIDTH_PX,
                                            height: A4_PAGE_HEIGHT_PX,
                                            transform: `scale(${scale})`,
                                            transformOrigin: 'top left',
                                        }}
                                    >
                                        <PageDropWrapper
                                            pageIndex={pageIndex}
                                            enabled={showOrganizer}
                                            data={ensured}
                                        >
                                            <div
                                                className="shadow-2xl print:shadow-none"
                                                data-cv-page={pageIndex}
                                                style={{ width: '210mm', height: '297mm' }}
                                            >
                                                <CVPreview
                                                    data={pageData}
                                                    fullData={ensured}
                                                    template={template}
                                                    isContinuationPage={isContinuation}
                                                    pageIndex={pageIndex}
                                                    totalPages={pagesToRender.length}
                                                />
                                            </div>
                                        </PageDropWrapper>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {showOrganizer && (
                    <div className="no-print" data-tour="organizer-panel">
                        <PageOrganizerPanel
                            data={ensured}
                            pageCount={Math.max(pageCount, 2)}
                            onMoveItem={onMoveItem}
                            onMoveSection={onMoveSection}
                        />
                    </div>
                )}
            </div>

            <DragOverlay>
                {activeLabel ? (
                    <div className="rounded-lg border border-indigo-300 bg-white px-3 py-2 text-xs font-semibold shadow-lg">
                        {activeLabel}
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

export default BuilderPreview;
